import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, CheckCircle, X, ArrowRight, Volume2, Zap, Plane, 
  Moon, DollarSign, Heart, Users, Loader2, AlertTriangle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FORMSPREE_ID = 'mvkpjgoe';

const comparisonData = [
  {
    feature: 'Comfort',
    oralAppliance: { text: 'Small mouthpiece, similar to a retainer', rating: 5 },
    cpap: { text: 'Mask strapped to face with headgear', rating: 2 },
  },
  {
    feature: 'Noise Level',
    oralAppliance: { text: 'Completely silent', rating: 5 },
    cpap: { text: 'Motor noise (even "quiet" models)', rating: 2 },
  },
  {
    feature: 'Travel Friendly',
    oralAppliance: { text: 'Fits in toiletry bag, no power needed', rating: 5 },
    cpap: { text: 'Bulky, needs power, TSA hassles', rating: 1 },
  },
  {
    feature: 'Partner Impact',
    oralAppliance: { text: 'Partner won\'t notice it', rating: 5 },
    cpap: { text: 'Noise and mask can disturb partner', rating: 2 },
  },
  {
    feature: 'Maintenance',
    oralAppliance: { text: 'Simple rinse and brush', rating: 5 },
    cpap: { text: 'Filters, tubing, mask replacement, distilled water', rating: 2 },
  },
  {
    feature: 'Compliance Rate',
    oralAppliance: { text: '90%+ use it every night', rating: 5 },
    cpap: { text: '~50% abandon within 1 year', rating: 2 },
  },
  {
    feature: 'Sleep Positions',
    oralAppliance: { text: 'Sleep in any position', rating: 5 },
    cpap: { text: 'Limited positions, mask can shift', rating: 2 },
  },
  {
    feature: 'Effectiveness',
    oralAppliance: { text: 'Proven for mild-moderate apnea', rating: 4 },
    cpap: { text: 'Gold standard for all severities', rating: 5 },
  },
];

const CPAPComparison = () => {
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '', cpapStatus: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'CPAP vs Oral Appliance Comparison | Sunshine Dental Care Roseville';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'CPAP Comparison Page Lead - High Intent',
          'Lead Type': 'CPAP Comparison Page',
          'CPAP Status': formData.cpapStatus,
          ...formData,
        }),
      });
      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="cpap-comparison-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <AlertTriangle size={16} />
              <span>Tired of Your CPAP?</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              CPAP vs Oral Appliance: <br className="hidden md:block" />
              <span className="text-cyan-400">The Honest Comparison</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              You were prescribed a CPAP. Maybe you tried it. Maybe you hated it. 
              Here's what your sleep doctor probably didn't tell you about the alternative.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#comparison" className="inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-8 py-4 rounded-xl transition-all">
                See the Comparison
                <ArrowRight size={20} />
              </a>
              <a href="#get-started" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-xl transition-all border border-white/20">
                I'm Ready to Switch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem with CPAP */}
      <section className="py-16 bg-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Let's Be Honest About CPAP
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Volume2, text: "The noise keeps your partner awake" },
              { icon: Moon, text: "The mask leaves red marks on your face" },
              { icon: Plane, text: "Traveling with it is a nightmare" },
              { icon: Zap, text: "You feel claustrophobic wearing it" },
              { icon: Users, text: "You've given up and it's collecting dust" },
              { icon: Heart, text: "Your intimacy has suffered" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                  <item.icon size={24} className="text-red-500" />
                </div>
                <p className="text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-8 text-gray-600">
            <strong>You're not alone.</strong> Studies show ~50% of CPAP users abandon their machines within the first year.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section id="comparison" className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Side-by-Side Comparison
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            See how oral appliances stack up against CPAP machines on the factors that matter most.
          </p>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-gray-600 font-semibold">Feature</th>
                  <th className="px-6 py-4 text-center bg-cyan-50">
                    <span className="text-cyan-700 font-bold">Oral Appliance</span>
                  </th>
                  <th className="px-6 py-4 text-center bg-gray-100">
                    <span className="text-gray-700 font-bold">CPAP Machine</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 font-medium text-gray-900">{row.feature}</td>
                    <td className="px-6 py-4 bg-cyan-50/50">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={18} className="text-cyan-600 shrink-0" />
                        <span className="text-gray-700 text-sm">{row.oralAppliance.text}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 bg-gray-50/50">
                      <div className="flex items-center gap-2">
                        {row.cpap.rating >= 4 ? (
                          <CheckCircle size={18} className="text-green-500 shrink-0" />
                        ) : (
                          <X size={18} className="text-red-400 shrink-0" />
                        )}
                        <span className="text-gray-600 text-sm">{row.cpap.text}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {comparisonData.map((row, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-gray-50 px-4 py-3 font-semibold text-gray-900">{row.feature}</div>
                <div className="grid grid-cols-2 divide-x divide-gray-100">
                  <div className="p-4 bg-cyan-50/30">
                    <p className="text-xs text-cyan-700 font-semibold mb-1">Oral Appliance</p>
                    <p className="text-sm text-gray-700">{row.oralAppliance.text}</p>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-gray-500 font-semibold mb-1">CPAP</p>
                    <p className="text-sm text-gray-600">{row.cpap.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Bottom Line */}
      <section className="py-16 bg-gradient-to-br from-cyan-600 to-cyan-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">The Bottom Line</h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            CPAP is effective — <em>if you use it</em>. But the best treatment is the one you'll actually wear every night. 
            For mild to moderate sleep apnea, oral appliances offer comparable results with dramatically better compliance.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 rounded-xl p-6">
              <p className="text-4xl font-bold text-white mb-2">90%+</p>
              <p className="text-cyan-200">Oral appliance users wear it nightly</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <p className="text-4xl font-bold text-white mb-2">~50%</p>
              <p className="text-cyan-200">CPAP users quit within 1 year</p>
            </div>
            <div className="bg-white/10 rounded-xl p-6">
              <p className="text-4xl font-bold text-white mb-2">500+</p>
              <p className="text-cyan-200">Patients helped by Dr. Yang</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Is an Oral Appliance Right for You?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100">
              <h3 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
                <CheckCircle size={20} />
                Great Candidates
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-1 shrink-0" />
                  Mild to moderate sleep apnea (AHI 5-30)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-1 shrink-0" />
                  CPAP intolerant — tried it, hated it
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-1 shrink-0" />
                  Frequent travelers or campers
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-1 shrink-0" />
                  Primary snorers without severe apnea
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-1 shrink-0" />
                  Those who want a simpler solution
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100">
              <h3 className="text-lg font-bold text-amber-700 mb-4 flex items-center gap-2">
                <AlertTriangle size={20} />
                May Need Discussion
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-500 mt-1 shrink-0" />
                  Severe sleep apnea (AHI 30+) — may still work
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-500 mt-1 shrink-0" />
                  Significant jaw joint (TMJ) issues
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-500 mt-1 shrink-0" />
                  Insufficient teeth for retention
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-500 mt-1 shrink-0" />
                  Central sleep apnea (different condition)
                </li>
              </ul>
              <p className="mt-4 text-sm text-gray-500">
                Not sure? Dr. Yang will evaluate your specific situation and give you an honest recommendation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section id="get-started" className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Ditch the CPAP?
              </h2>
              <p className="text-gray-300 mb-6">
                Schedule a free consultation with Dr. Maichou Yang. We'll review your sleep study, 
                examine your airway, and tell you honestly if an oral appliance is right for you.
              </p>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-cyan-400" />
                  <span>Free consultation — no obligation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-cyan-400" />
                  <span>Most medical insurance accepted</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-cyan-400" />
                  <span>Serving Roseville, Rocklin, Folsom & beyond</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl" data-testid="comparison-form">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Get Your Free Evaluation</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your CPAP Status</label>
                    <select
                      required
                      value={formData.cpapStatus}
                      onChange={(e) => setFormData({...formData, cpapStatus: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                      data-testid="cpap-status-select"
                    >
                      <option value="">Select one...</option>
                      <option value="tried-quit">Tried CPAP, gave up</option>
                      <option value="using-hate">Currently using, but hate it</option>
                      <option value="prescribed-never-started">Prescribed but never started</option>
                      <option value="researching">Just researching alternatives</option>
                      <option value="no-cpap">Never had CPAP, newly diagnosed</option>
                    </select>
                  </div>

                  <input
                    type="text"
                    placeholder="First Name"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none"
                  />

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold py-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={20} className="animate-spin" /> Sending...</>
                    ) : (
                      <>Get My Free Evaluation</>
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-8" data-testid="form-success">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">You're All Set!</h3>
                  <p className="text-gray-600">We'll contact you within 24 hours to schedule your free consultation.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Call CTA */}
      <section className="py-8 bg-cyan-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-white text-lg mb-4">Prefer to talk now? We're happy to answer your questions.</p>
          <a
            href="tel:9167731122"
            className="inline-flex items-center gap-2 bg-white text-cyan-600 font-bold px-8 py-3 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <Phone size={20} />
            Call (916) 773-1122
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CPAPComparison;
